import { Button, Popconfirm, InputNumber, Input, Space, Typography } from "antd";
import {
  PlusOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

interface Props {
  isTerminated: boolean;
  onOpenForm: (mode: "record" | "reactivate") => void;
  formOpen: "record" | "reactivate" | null;
  onCloseForm: () => void;
  amount: number | null;
  onAmountChange: (value: number | null) => void;
  method: string;
  onMethodChange: (value: string) => void;
  reference: string;
  onReferenceChange: (value: string) => void;
  note: string;
  onNoteChange: (value: string) => void;
  onRecord: () => void;
  onTerminate: () => void;
  onReactivate: () => void;
  recordPending: boolean;
  terminatePending: boolean;
  reactivatePending: boolean;
}

export const TenantPaymentActions: React.FC<Props> = ({
  isTerminated,
  onOpenForm,
  formOpen,
  onCloseForm,
  amount,
  onAmountChange,
  method,
  onMethodChange,
  reference,
  onReferenceChange,
  note,
  onNoteChange,
  onRecord,
  onTerminate,
  onReactivate,
  recordPending,
  terminatePending,
  reactivatePending,
}) => {
  return (
    <>
      <Space wrap>
        {!isTerminated ? (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => onOpenForm("record")}
              loading={recordPending}
            >
              Record Payment
            </Button>
            <Popconfirm
              title="Terminate this tenant?"
              description="This disables the system for the tenant (is_terminated + is_enable). The package is preserved for easy reactivation."
              onConfirm={onTerminate}
              okText="Terminate"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<StopOutlined />}
                loading={terminatePending}
              >
                Terminate (One Click)
              </Button>
            </Popconfirm>
          </>
        ) : (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => onOpenForm("reactivate")}
            loading={reactivatePending}
          >
            Reactivate & Record Payment
          </Button>
        )}
      </Space>

      {formOpen && (
        <div className="rounded-md border border-gray-200 p-3 flex flex-col gap-2 bg-gray-50">
          <Text strong>
            {formOpen === "record"
              ? "Record a payment for the current cycle"
              : "Reactivate tenant & record payment"}
          </Text>
          <div className="flex items-center gap-2">
            <Text className="w-20 text-xs text-gray-500">Amount</Text>
            <InputNumber
              className="flex-1"
              prefix="$"
              min={0}
              value={amount}
              onChange={(v) => onAmountChange(typeof v === "number" ? v : null)}
              placeholder="Amount"
            />
          </div>
          <div className="flex items-center gap-2">
            <Text className="w-20 text-xs text-gray-500">Method</Text>
            <Input
              className="flex-1"
              value={method}
              onChange={(e) => onMethodChange(e.target.value)}
              placeholder="e.g. Bank Transfer, Cash, Card"
            />
          </div>
          <div className="flex items-center gap-2">
            <Text className="w-20 text-xs text-gray-500">Reference</Text>
            <Input
              className="flex-1"
              value={reference}
              onChange={(e) => onReferenceChange(e.target.value)}
              placeholder="Transaction / receipt reference"
            />
          </div>
          <div className="flex items-center gap-2">
            <Text className="w-20 text-xs text-gray-500">Note</Text>
            <Input
              className="flex-1"
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Reason / note"
            />
          </div>
          <Space>
            <Button
              type="primary"
              loading={
                formOpen === "record" ? recordPending : reactivatePending
              }
              onClick={formOpen === "record" ? onRecord : onReactivate}
            >
              {formOpen === "record" ? "Save Payment" : "Reactivate"}
            </Button>
            <Button onClick={onCloseForm}>Cancel</Button>
          </Space>
        </div>
      )}
    </>
  );
};
